import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useSEO } from '../utils/seo';
import { useCopy, useLocale } from '../i18n/LocaleContext';

const COPY = {
  fr: {
    seoTitle: 'Connexion — Le Placard à Doudous', seoDescription: 'Connectez-vous à votre espace.', welcome: name => `Bienvenue, ${name} !`, error: 'Erreur de connexion',
    subtitle: 'Content(e) de vous revoir !', email: 'Email', emailPlaceholder: 'votre@email.fr', password: 'Mot de passe',
    hidePassword: 'Masquer le mot de passe', showPassword: 'Afficher le mot de passe', forgot: 'Mot de passe oublié ?', loading: 'Connexion...', submit: 'Se connecter',
    noAccount: 'Pas encore de compte ?', register: 'Créer un compte',
  },
  en: {
    seoTitle: 'Log in — Le Placard à Doudous', seoDescription: 'Log in to your account.', welcome: name => `Welcome, ${name}!`, error: 'Connection error',
    subtitle: 'Lovely to see you again!', email: 'Email', emailPlaceholder: 'your@email.com', password: 'Password',
    hidePassword: 'Hide password', showPassword: 'Show password', forgot: 'Forgot your password?', loading: 'Logging in...', submit: 'Log in',
    noAccount: 'No account yet?', register: 'Create an account',
  },
};

export default function Login() {
  const c = useCopy(COPY);
  const { lp } = useLocale();
  useSEO(c.seoTitle, c.seoDescription);
  const [form, setForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(form.email, form.password);
      toast.success(c.welcome(user.name));
      navigate(user.is_admin ? '/admin' : lp('/compte'));
    } catch (err) {
      toast.error(err.message || c.error);
    }
    setLoading(false);
  };

  return (
    <div className="hero-mist noise min-h-screen flex items-center justify-center pt-20 pb-16">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md mx-4"
      >
        <div className="rounded-3xl bg-card p-8 shadow-ambient sm:p-10">
          <div className="text-center mb-8">
            <Link to={lp('/')} className="font-serif text-2xl text-foreground">Le Placard à Doudous</Link>
            <p className="font-sans text-sm text-[hsl(var(--muted-foreground))] mt-2">{c.subtitle}</p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="font-sans text-sm">{c.email}</Label>
              <Input id="email" type="email" autoComplete="email" value={form.email} onChange={e => setForm(p => ({...p, email: e.target.value}))} required className="rounded-xl font-sans" placeholder={c.emailPlaceholder} data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="font-sans text-sm">{c.password}</Label>
              <div className="relative">
                <Input id="password" autoComplete="current-password" type={showPwd ? 'text' : 'password'} value={form.password} onChange={e => setForm(p => ({...p, password: e.target.value}))} required className="rounded-xl font-sans pr-10" data-testid="login-password-input" />
                <button type="button" aria-label={showPwd ? c.hidePassword : c.showPassword} aria-pressed={showPwd} onClick={() => setShowPwd(!showPwd)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="text-right">
                <Link to={lp('/mot-de-passe-oublie')} className="font-sans text-xs text-muted-foreground hover:text-foreground transition-colors duration-200">{c.forgot}</Link>
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full rounded-full bg-primary text-primary-foreground font-sans font-medium shadow-[var(--shadow-soft)] hover:shadow-[var(--shadow-ambient)] transition-[box-shadow] duration-200" data-testid="login-submit-button">
              {loading ? c.loading : c.submit}
            </Button>
          </form>
          <p className="font-sans text-sm text-center text-[hsl(var(--muted-foreground))] mt-6">
            {c.noAccount}{' '}
            <Link to={lp('/inscription')} className="text-[hsl(var(--primary))] font-medium hover:underline">{c.register}</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
